import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig, fetchJSONWithTimeout } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

// A browser abort cannot cancel a cart write already accepted by Shopify. Keep
// one discount write in flight across the drawer and full-cart instances.
let discountMutationPending = false;
const discountSectionsNeedingRefresh = new Set();

class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (discountMutationPending || discountSectionsNeedingRefresh.has(this.dataset.sectionId)) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const input = form.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;
    const code = input.value.trim();
    const existing = this.#existingDiscounts();
    if (!code || existing.some((value) => value.toLowerCase() === code.toLowerCase())) return;
    await this.#update([...existing, code], event, { code, input, existing });
  };

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (discountMutationPending || discountSectionsNeedingRefresh.has(this.dataset.sectionId)) return;
    if (event instanceof KeyboardEvent && !['Enter', ' '].includes(event.key)) return;
    if (!(event.target instanceof HTMLElement)) return;
    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement) || !pill.dataset.discountCode) return;
    const existing = this.#existingDiscounts();
    if (!existing.includes(pill.dataset.discountCode)) return;
    await this.#update(existing.filter((code) => code !== pill.dataset.discountCode), event);
  };

  async #update(codes, event, applied) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    discountMutationPending = true;
    const controls = Array.from(document.querySelectorAll('cart-discount-component input, cart-discount-component button'));
    const disabled = controls.map((control) => control.disabled);
    controls.forEach((control) => { control.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    this.refs.cartDiscountError.classList.add('hidden');
    this.refs.cartDiscountErrorDiscountCode.classList.add('hidden');
    this.refs.cartDiscountErrorShipping.classList.add('hidden');
    this.querySelector('[data-discount-recovery]')?.remove();
    const deferred = CartDiscountUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: codes.map((code) => ({ code })), promise: deferred.promise }));
    try {
      const data = await fetchJSONWithTimeout(Theme.routes.cart_update_url, fetchConfig('json', {
        body: JSON.stringify({ discount: codes.join(','), sections: [sectionId] }),
      }));
      if (!Array.isArray(data.discount_codes) || !Array.isArray(data.items)) throw new Error('Invalid cart response');
      const html = data.sections?.[sectionId];
      if (typeof html !== 'string') throw new Error('Missing cart section');
      const section = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${sectionId}`);
      if (!section) throw new Error('Invalid cart section');
      const added = applied && data.discount_codes.find((discount) => discount.code.toLowerCase() === applied.code.toLowerCase());
      if (applied && added?.applicable === false) {
        applied.input.value = '';
        this.#handleDiscountError('discount_code');
      } else if (applied && added?.applicable === true && this.#sameVisibleCodes(section, applied.existing)) {
        applied.input.value = '';
        this.#handleDiscountError('shipping');
      } else {
        await morphSection(sectionId, html, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
        if (applied) applied.input.value = '';
      }
      deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
    } catch (error) {
      // Read back the real cart and markup. Never retry an uncertain mutation.
      try {
        const cart = await fetchJSONWithTimeout(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!Array.isArray(cart.items) || !Array.isArray(cart.discount_codes)) throw new Error('Invalid cart reconciliation');
        await sectionRenderer.renderSection(sectionId, { cache: false, mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
        deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart) });
      } catch (recoveryError) {
        discountSectionsNeedingRefresh.add(sectionId);
        deferred.reject(recoveryError);
      }
      this.#showRecovery();
      this.dispatchEvent(new CartErrorEvent({ error: Theme.translations.discount_update_error, code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      discountMutationPending = false;
      this.removeAttribute('aria-busy');
      controls.forEach((control, index) => { control.disabled = disabled[index]; });
      if (discountSectionsNeedingRefresh.has(sectionId)) {
        document.querySelectorAll(`cart-discount-component[data-section-id="${CSS.escape(sectionId)}"] input, cart-discount-component[data-section-id="${CSS.escape(sectionId)}"] button`)
          .forEach((control) => { control.disabled = true; });
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  }

  #showRecovery() {
    // Morph may replace this element; place feedback on the current instance.
    const target = document.querySelector(`cart-discount-component[data-section-id="${CSS.escape(this.dataset.sectionId)}"]`) || this;
    const notice = document.createElement('p');
    notice.dataset.discountRecovery = '';
    notice.setAttribute('role', 'alert');
    notice.append(document.createTextNode(Theme.translations.discount_update_error + ' '));
    const link = document.createElement('a');
    link.href = Theme.routes.cart_url;
    link.textContent = Theme.translations.cart_view;
    notice.append(link);
    target.append(notice);
  }

  #sameVisibleCodes(section, existing) {
    const codes = Array.from(section.querySelectorAll('.cart-discount__pill')).map((pill) => pill.dataset.discountCode);
    return codes.length === existing.length && codes.every((code) => existing.includes(code));
  }

  #handleDiscountError(type) {
    const target = type === 'discount_code' ? this.refs.cartDiscountErrorDiscountCode : this.refs.cartDiscountErrorShipping;
    this.refs.cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    this.dispatchEvent(new CartErrorEvent({ error: target.textContent.trim(), code: 'VALIDATION_CUSTOM' }));
  }

  #existingDiscounts() {
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .filter((pill) => pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string')
      .map((pill) => pill.dataset.discountCode);
  }
}

if (!customElements.get('cart-discount-component')) customElements.define('cart-discount-component', CartDiscount);
